import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import SafeHtml from "@/components/base/SafeHtml";
import { usePageContent } from "@/hooks/usePageContent";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "About Us", item: "https://cashmyproperty.ai/about" },
  ],
};

const values = [
  {
    icon: "ri-shield-check-line",
    title: "Transparency",
    desc: "No hidden agendas in valuation or partnerships. Every recommendation, fee, and projection is fully disclosed upfront.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Data-Driven",
    desc: "Decisions based on market analytics, not guesswork. Our AI engine pulls from 40+ live data feeds to ensure accuracy.",
  },
  {
    icon: "ri-plant-line",
    title: "Sustainability",
    desc: "Promoting development that suits the local ecosystem. We recommend land uses that protect the environment and community.",
  },
  {
    icon: "ri-user-heart-line",
    title: "Owner-Centric",
    desc: "Protecting the landowner's interest above all. You retain ownership, you choose the path, and you control the timeline.",
  },
];

const milestones = [
  { year: "2010", title: "Govianu Group Founded", desc: "Started as a boutique real estate consultancy in Bengaluru, Karnataka." },
  { year: "2018", title: "10,000+ Acres Consulted", desc: "Expanded operations across South India, advising on land acquisition, development, and JDA structuring." },
  { year: "2023", title: "CashMyProperty.ai Launched", desc: "Pioneered India's first AI-powered land monetization platform, combining data science with real estate expertise." },
  { year: "2026", title: "₹500 Cr+ Value Unlocked", desc: "Platform-wide cumulative value unlocked for landowners across 25+ Indian states." },
];

export default function AboutPage() {
  const { getContent } = usePageContent('about');
  const missionHeading = getContent('about-mission-heading') || 'Unlock the Hidden Gold in Your Dirt';
  const missionBody = getContent('about-mission-body');
  const visionHeading = getContent('about-vision-heading') || 'The Global Standard for AI-Driven Land Monetization';
  const visionBody = getContent('about-vision-body');
  const heroImage = getContent('about-hero-image') || 'https://readdy.ai/api/search-image?query=Warm%20golden%20hour%20aerial%20view%20of%20developing%20suburban%20landscape%20with%20scattered%20land%20plots%20green%20fields%20and%20connecting%20road%20networks%2C%20soft%20natural%20sunlight%20casting%20long%20shadows%2C%20professional%20real%20estate%20development%20photography%2C%20warm%20amber%20gold%20and%20cream%20tones%2C%20sophisticated%20corporate%20aesthetic%2C%20India%20suburban%20landscape%2C%20high%20detail%2C%20no%20people%2C%20editorial%20quality&width=1800&height=800&seq=about-hero-landscape-02&orientation=landscape&nocache=true';

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <PageHero
        badge="About Us"
        title="India's First"
        highlight="AI Land Monetization"
        subtitle="Powered by the Govianu Group's 15+ years of real estate excellence."
        image={heroImage}
      />

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">About Us</span>
          </nav>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider">
              Our Mission
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950">
              {missionHeading}
            </h2>
            {missionBody && missionBody.includes('<p>') ? (
              <SafeHtml html={missionBody} className="text-foreground-600 text-lg leading-relaxed space-y-4" />
            ) : (
              <p className="text-foreground-600 text-lg leading-relaxed">
                {missionBody || 'To democratize access to sophisticated real estate development intelligence, empowering landowners to convert dead assets into generational wealth.'}
              </p>
            )}
            <p className="text-foreground-600 leading-relaxed">
              Millions of acres of land across India sit idle because owners lack the capital
              or expertise to develop them. CashMyProperty.ai was built to change that.
              By combining artificial intelligence with the Govianu Group's deep real estate
              expertise, we analyze your land's true potential and connect you with the right
              developers, brands, and investors — all without requiring you to sell.
            </p>
            <p className="text-foreground-600 leading-relaxed">
              Whether you're an NRI managing property remotely, a family sitting on ancestral
              land, or a retired senior who is asset-rich but cash-poor — our AI feasibility
              engine calculates the <strong className="text-foreground-950">Highest &amp; Best Use</strong> of
              your property and delivers a comprehensive monetization roadmap within 48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider">
              Our Vision
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950">
              {visionHeading}
            </h2>
            {visionBody && visionBody.includes('<p>') ? (
              <SafeHtml html={visionBody} className="text-foreground-600 text-lg leading-relaxed space-y-4" />
            ) : (
              <p className="text-foreground-600 text-lg leading-relaxed">
                {visionBody || 'To become the global standard for AI-driven land monetization and strategic property development intelligence — trusted by landowners, developers, and brands worldwide.'}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              The Problem &amp; Solution
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              Why Landowners Need Us
            </h2>
            <p className="text-foreground-600">
              Millions of acres of land in India sit idle. Here is why — and what we do about it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {/* Problems */}
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-lg text-foreground-950 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-800/10">
                  <i className="ri-error-warning-line text-foreground-600" />
                </span>
                The Problem
              </h3>
              {[
                {
                  title: "Idle Assets",
                  desc: "Millions of acres of land sit idle because owners lack the capital or expertise to develop them.",
                },
                {
                  title: "Information Asymmetry",
                  desc: "Landowners often do not know the true commercial potential of their land. Is it better for a warehouse, a petrol pump, or a condo?",
                },
                {
                  title: "Fear of Exploitation",
                  desc: "Owners fear losing land to developers or getting locked into bad contracts with unfavorable terms.",
                },
                {
                  title: "Liquidity Issues",
                  desc: "Land is asset-heavy but cash-flow poor. Owners need income, not another piece of paper.",
                },
              ].map((p) => (
                <div key={p.title} className="p-5 rounded-xl bg-background-200/50 border border-background-300/30">
                  <h4 className="font-semibold text-foreground-950 mb-1">{p.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>

            {/* Solutions */}
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-lg text-foreground-950 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-lightbulb-line text-primary-500" />
                </span>
                Our Solution
              </h3>
              {[
                {
                  title: "AI-Based Feasibility",
                  desc: "We use AI to analyze location demographics, traffic patterns, and zoning laws to recommend the most profitable use case for your land.",
                  icon: "ri-cpu-line",
                },
                {
                  title: "End-to-End Execution",
                  desc: "We do not just advise — we facilitate the deal. From JDA structuring to lease agreements, we handle the entire process.",
                  icon: "ri-file-list-3-line",
                },
                {
                  title: "Vetted Network",
                  desc: "Access to a verified pool of developers, retail brands, and corporate tenants actively seeking land across India.",
                  icon: "ri-shield-star-line",
                },
              ].map((s) => (
                <div key={s.title} className="p-5 rounded-xl card-dark hover:border-primary-500/40 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className={`${s.icon} text-xl text-primary-500`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-950 mb-1">{s.title}</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Connecting statement */}
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-primary-500/10 border border-primary-500/20">
              <i className="ri-arrow-down-line text-primary-500" />
              <span className="text-sm text-primary-700 font-medium">
                We bridge the gap between raw land and sophisticated commercial demand.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              What Drives Us
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="p-6 rounded-2xl card-dark text-center group hover:border-primary-500/40 transition-all duration-300 h-full"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5 group-hover:border-primary-500/60 transition-colors">
                  <i className={`${v.icon} text-2xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground-950 mb-2">
                  {v.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              From Consultancy to AI Platform
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-8">
            {milestones.map((m, i) => (
              <div key={m.year} className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-700/30 shrink-0">
                    <span className="text-sm font-display font-bold text-primary-500">
                      {m.year}
                    </span>
                  </div>
                  {i < milestones.length - 1 && (
                    <div className="w-px flex-1 bg-primary-900/20 mt-3" />
                  )}
                </div>
                <div className="pb-8">
                  <h3 className="font-display font-semibold text-lg text-foreground-950 mb-1">
                    {m.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Who We Serve
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              Built for Every Kind of Landowner
            </h2>
            <p className="text-foreground-600">
              Our platform is designed for the people who own land but cannot unlock its value alone.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {[
              {
                icon: "ri-home-4-line",
                title: 'The "Inheritors"',
                desc: "Individuals who inherited land but have 9-to-5 jobs and no time to manage development. We handle everything while they focus on their career.",
                stat: "Largest segment",
              },
              {
                icon: "ri-global-line",
                title: "NRIs",
                desc: "Non-Resident Indians owning land back home but worried about encroachment and mismanagement. A trusted partner to monetize remotely.",
                stat: "Remote management",
              },
              {
                icon: "ri-heart-pulse-line",
                title: "Retired Seniors",
                desc: "Asset-rich, cash-poor. They need monthly rental income from their land — not an empty plot that demands maintenance and taxes.",
                stat: "Passive income focus",
              },
              {
                icon: "ri-first-aid-kit-line",
                title: "Distressed Asset Owners",
                desc: "People needing immediate liquidity without selling the underlying land. We structure deals that generate cash flow while keeping the asset.",
                stat: "Urgent liquidity",
              },
            ].map((persona) => (
              <div
                key={persona.title}
                className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300 h-full flex flex-col"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mb-5">
                  <i className={`${persona.icon} text-2xl text-primary-500`} />
                </div>
                <span className="inline-block text-xs font-medium px-3 py-1 rounded-full bg-primary-500/10 text-primary-700 mb-3 w-fit">
                  {persona.stat}
                </span>
                <h3 className="font-display font-semibold text-lg text-foreground-950 mb-2">
                  {persona.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed flex-1">{persona.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Govianu Group */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
                Parent Company
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Backed by the Govianu Group
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                CashMyProperty.ai is a technology venture of the Govianu Group, a Bengaluru-based real
                estate consultancy with 15+ years of experience in land acquisition, development
                structuring, and joint venture agreements across South India.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                The Group has advised on 10,000+ acres of land transactions, structured 200+ JDAs, and
                maintains relationships with leading developers, QSR brands, and institutional investors.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                  <div className="text-2xl font-display font-bold text-foreground-950">15+</div>
                  <div className="text-sm text-foreground-500">Years of Experience</div>
                </div>
                <div className="p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                  <div className="text-2xl font-display font-bold text-foreground-950">200+</div>
                  <div className="text-sm text-foreground-500">JDAs Structured</div>
                </div>
                <div className="p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                  <div className="text-2xl font-display font-bold text-foreground-950">25+</div>
                  <div className="text-sm text-foreground-500">States Covered</div>
                </div>
                <div className="p-4 rounded-xl bg-background-200/50 border border-background-300/30">
                  <div className="text-2xl font-display font-bold text-foreground-950">₹500 Cr+</div>
                  <div className="text-sm text-foreground-500">Value Unlocked</div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20corporate%20real%20estate%20consultancy%20office%20interior%20in%20Bengaluru%20India%2C%20glass%20meeting%20rooms%2C%20professional%20team%20working%20on%20land%20development%20plans%2C%20dramatic%20dark%20moody%20lighting%2C%20teal%20and%20amber%20accent%20lights%2C%20cinematic%20business%20photography%2C%20highly%20detailed&width=800&height=600&seq=about-govianu-01&orientation=landscape"
                alt="Govianu Group Office"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Ready to Unlock Your Land's Potential?
          </h2>
          <p className="text-foreground-600 mb-8">
            Get your AI Feasibility Report and see exactly what your land is worth.
          </p>
          <Link
            to="/feasibility-report"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Get Your AI Report
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}